import { get } from "../api.js";
import { shortenNames, type TutorOption, type Whose } from "../calendar/entries.js";

/**
 * Whose figures a report can be run for.
 *
 * The reports are keyed by Amelia provider, because that is the identity
 * FastAPI shares with Amelia; who that provider *is* belongs to the Hub — which
 * `tutorsByProvider` in `staff.ts` used to answer half of, and `namesByProvider`
 * below now answers all of, so that the row of boxes and the names in the
 * earnings table cannot come from two separate reads of one list. So the
 * list of people worth offering is the Hub's tutor list, narrowed to those who
 * have been linked to a provider — somebody in the office holds a tutor profile
 * because that is how a staff account is held here, and has no provider because
 * they do not teach. Offering them would be offering a box that empties every
 * report and explains nothing.
 *
 * The tick boxes themselves are the diary's, deliberately: `isTicked`,
 * `toggleAll` and `toggleTutor` in `calendar/entries.ts` already decide what a
 * half-filled row of names means, and a second answer to that question on
 * another page is a second set of edge cases. This module only turns tutors
 * into the options those functions take, and turns a row of ticks back into the
 * provider ids a report is asked for.
 */

export interface ReportPerson {
  providerId: number;
  name: string;
  /** They have left. Still offered: a past period still has their lessons in it. */
  left: boolean;
}

interface TutorRow {
  id: string;
  displayName: string;
  externalProviderId: number | null;
  left?: boolean;
}

/**
 * Everybody a report can be asked about.
 *
 * Leavers are included rather than filtered out, which is the opposite of what
 * a "who works here" list wants and the right answer here: a report over last
 * spring is a report over lessons somebody taught before they left, and a
 * filter that cannot name them is a filter that quietly loses their earnings
 * from the comparison. They are labelled instead — see `peopleOptions`.
 */
export async function reportPeople(): Promise<ReportPerson[]> {
  const { tutors } = await get<{ tutors: TutorRow[] }>("/admin/tutors");
  return tutors
    .filter((tutor) => tutor.externalProviderId !== null)
    .map((tutor) => ({
      providerId: tutor.externalProviderId as number,
      name: tutor.displayName,
      left: tutor.left === true,
    }));
}

/** Provider ids against the full names the earnings and timetable tables use. */
export function namesByProvider(people: readonly ReportPerson[]): Map<number, string> {
  return new Map(people.map((person) => [person.providerId, person.name]));
}

/**
 * The row of boxes, first names only until two of them clash.
 *
 * The same shortening the diary uses, for the same reason — a filter is read at
 * a glance and surnames are what turn it into a paragraph — and from the same
 * function, so a school with two Matts gets the same two labels on both pages.
 *
 * "(left)" is the one thing added. Somebody who has left is worth reporting on
 * and is not worth mistaking for a colleague still teaching, and the difference
 * is invisible in a first name.
 */
export function peopleOptions(people: readonly ReportPerson[]): TutorOption[] {
  const labels = shortenNames(people.map((person) => person.name));

  return people
    .map((person, index) => ({
      value: String(person.providerId),
      label: `${labels[index] || "Unnamed tutor"}${person.left ? " (left)" : ""}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Which providers to ask about, or nothing at all when it is everybody.
 *
 * Null is not an empty selection: it means the reports are asked the way they
 * were before anybody ticked a box — one whole-school question, one answer —
 * and it is what keeps the ordinary case a single request per report. An empty
 * array is the other real state, every box cleared, and the page says so rather
 * than drawing six panels of nothing.
 */
export function chosenProviders(whose: Whose, options: readonly TutorOption[]): number[] | null {
  if (whose === "all") return null;
  return options
    .filter((option) => whose.has(option.value))
    .map((option) => Number(option.value))
    .filter((id) => Number.isInteger(id) && id > 0);
}

/**
 * Who the figures on screen are for, in a sentence.
 *
 * Named rather than counted while the list is short enough to read, because
 * "Gary, Matt and Sam" is the line somebody checks before quoting a number and
 * "3 members of staff" is not. Past three it stops being readable and the count
 * is the better half of the answer.
 */
export function describeChosen(
  providers: readonly number[],
  options: readonly TutorOption[],
): string {
  const chosen = options.filter((option) => providers.includes(Number(option.value)));
  const labels = chosen.map((option) => option.label);

  if (labels.length === 0) return "Nobody";
  if (labels.length === 1) return labels[0] as string;
  if (labels.length <= 3) {
    return `${labels.slice(0, -1).join(", ")} and ${labels.at(-1) as string}`;
  }

  const others = labels.length - 2;
  return `${labels.slice(0, 2).join(", ")} and ${others} others`;
}
